function getGlobalValue(name) {
  if (typeof window !== "undefined" && name in window) return window[name];
  return undefined;
}

const PRIVATE_QUOTA_THRESHOLD_BYTES = 1024 * 1024 * 1024;
const FILESYSTEM_PROBE_TIMEOUT_MS = 500;

export function probeStorageArea(storage, name) {
  if (!storage) {
    return { name, available: false, error: "Storage is unavailable." };
  }

  const key = `inssa:storage-probe:${name}:${Date.now()}:${Math.random()}`;
  const value = "1";
  try {
    storage.setItem(key, value);
    const stored = storage.getItem(key);
    storage.removeItem(key);
    if (stored !== value) {
      return { name, available: false, error: "Storage read did not match write." };
    }
    return { name, available: true, error: "" };
  } catch (error) {
    return {
      name,
      available: false,
      error: error?.message || "Storage write failed.",
    };
  }
}

export function probeBrowserSessionCapabilities(overrides = {}) {
  const localStorageProbe = probeStorageArea(
    Object.prototype.hasOwnProperty.call(overrides, "localStorage")
      ? overrides.localStorage
      : getGlobalValue("localStorage"),
    "localStorage"
  );
  const sessionStorageProbe = probeStorageArea(
    Object.prototype.hasOwnProperty.call(overrides, "sessionStorage")
      ? overrides.sessionStorage
      : getGlobalValue("sessionStorage"),
    "sessionStorage"
  );
  const authPersistenceMode = String(overrides.authPersistenceMode || "unknown");
  const authPersistenceError = String(overrides.authPersistenceError || "");
  const navigatorValue = Object.prototype.hasOwnProperty.call(overrides, "navigator")
    ? overrides.navigator
    : getGlobalValue("navigator");
  const notificationValue = Object.prototype.hasOwnProperty.call(overrides, "Notification")
    ? overrides.Notification
    : getGlobalValue("Notification");
  const privateBrowsingLikely = Boolean(overrides.privateBrowsingLikely);
  const privateBrowsingReason = String(overrides.privateBrowsingReason || "");
  const storageQuota = Number(overrides.storageQuota);

  const isLimitedSession =
    !localStorageProbe.available ||
    !sessionStorageProbe.available ||
    privateBrowsingLikely ||
    authPersistenceMode === "memory" ||
    authPersistenceMode === "failed";

  return {
    localStorage: localStorageProbe,
    sessionStorage: sessionStorageProbe,
    authPersistenceMode,
    authPersistenceError,
    privateBrowsingLikely,
    privateBrowsingReason,
    storageQuota: Number.isFinite(storageQuota) ? storageQuota : null,
    isLimitedSession,
    serviceWorkerSupported: Boolean(navigatorValue?.serviceWorker),
    notificationsSupported: Boolean(notificationValue),
  };
}

export async function detectPrivateBrowsingSignals(overrides = {}) {
  const navigatorValue = Object.prototype.hasOwnProperty.call(overrides, "navigator")
    ? overrides.navigator
    : getGlobalValue("navigator");
  const windowValue = Object.prototype.hasOwnProperty.call(overrides, "window")
    ? overrides.window
    : getGlobalValue("window");
  const estimate = navigatorValue?.storage?.estimate;
  const opfsSignal = await detectOpfsPrivateBrowsingSignal(navigatorValue);

  if (opfsSignal.privateBrowsingLikely) {
    return opfsSignal;
  }

  const filesystemSignal = await detectTemporaryFilesystemSignal(windowValue);

  if (filesystemSignal.privateBrowsingLikely) {
    return filesystemSignal;
  }

  const webkitStorageSignal = await detectWebkitStorageQuotaSignal({
    navigatorValue,
    performanceValue: windowValue?.performance,
  });

  if (webkitStorageSignal.privateBrowsingLikely) {
    return webkitStorageSignal;
  }

  if (typeof estimate !== "function") {
    return {
      privateBrowsingLikely: false,
      privateBrowsingReason: "",
      storageQuota: null,
      ...filesystemSignal,
      ...opfsSignal,
      ...webkitStorageSignal,
    };
  }

  try {
    const result = await estimate.call(navigatorValue.storage);
    const quota = Number(result?.quota);
    const privateBrowsingLikely =
      Number.isFinite(quota) &&
      quota > 0 &&
      quota <= PRIVATE_QUOTA_THRESHOLD_BYTES;

    return {
      privateBrowsingLikely,
      privateBrowsingReason: privateBrowsingLikely
        ? "Temporary browser storage quota is very small."
        : filesystemSignal.privateBrowsingReason ||
          opfsSignal.privateBrowsingReason ||
          webkitStorageSignal.privateBrowsingReason ||
          "",
      storageQuota: Number.isFinite(quota) ? quota : null,
      opfsAvailable: opfsSignal.opfsAvailable,
      filesystemAvailable: filesystemSignal.filesystemAvailable,
      webkitStorageQuota: webkitStorageSignal.webkitStorageQuota,
      jsHeapSizeLimit: webkitStorageSignal.jsHeapSizeLimit,
    };
  } catch (error) {
    return {
      privateBrowsingLikely: false,
      privateBrowsingReason:
        filesystemSignal.privateBrowsingReason ||
        opfsSignal.privateBrowsingReason ||
        webkitStorageSignal.privateBrowsingReason ||
        "",
      storageQuota: null,
      opfsAvailable: opfsSignal.opfsAvailable,
      filesystemAvailable: filesystemSignal.filesystemAvailable,
      webkitStorageQuota: webkitStorageSignal.webkitStorageQuota,
      jsHeapSizeLimit: webkitStorageSignal.jsHeapSizeLimit,
      storageEstimateError: error?.message || "Storage estimate failed.",
    };
  }
}

async function detectOpfsPrivateBrowsingSignal(navigatorValue) {
  const getDirectory = navigatorValue?.storage?.getDirectory;
  if (typeof getDirectory !== "function") {
    return {
      privateBrowsingLikely: false,
      privateBrowsingReason: "",
      opfsAvailable: null,
    };
  }

  try {
    await getDirectory.call(navigatorValue.storage);
    return {
      privateBrowsingLikely: false,
      privateBrowsingReason: "",
      opfsAvailable: true,
    };
  } catch (error) {
    return {
      privateBrowsingLikely: true,
      privateBrowsingReason:
        error?.message || error?.name || "Origin private file system access is blocked.",
      opfsAvailable: false,
    };
  }
}

function detectTemporaryFilesystemSignal(windowValue) {
  const requestFileSystem = windowValue?.webkitRequestFileSystem;
  if (typeof requestFileSystem !== "function") {
    return Promise.resolve({
      privateBrowsingLikely: false,
      privateBrowsingReason: "",
      filesystemAvailable: null,
    });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({
        privateBrowsingLikely: false,
        privateBrowsingReason: "",
        filesystemAvailable: null,
      });
    }, FILESYSTEM_PROBE_TIMEOUT_MS);

    try {
      requestFileSystem.call(
        windowValue,
        windowValue?.TEMPORARY || 0,
        1,
        () => {
          clearTimeout(timer);
          finish({
            privateBrowsingLikely: false,
            privateBrowsingReason: "",
            filesystemAvailable: true,
          });
        },
        (error) => {
          clearTimeout(timer);
          finish({
            privateBrowsingLikely: true,
            privateBrowsingReason:
              error?.message || "Temporary filesystem access is blocked.",
            filesystemAvailable: false,
          });
        }
      );
    } catch (error) {
      clearTimeout(timer);
      finish({
        privateBrowsingLikely: true,
        privateBrowsingReason: error?.message || "Temporary filesystem access failed.",
        filesystemAvailable: false,
      });
    }
  });
}

function detectWebkitStorageQuotaSignal({ navigatorValue, performanceValue }) {
  const temporaryStorage = navigatorValue?.webkitTemporaryStorage;
  const queryUsageAndQuota = temporaryStorage?.queryUsageAndQuota;
  if (typeof queryUsageAndQuota !== "function") {
    return Promise.resolve({
      privateBrowsingLikely: false,
      privateBrowsingReason: "",
      webkitStorageQuota: null,
      jsHeapSizeLimit: null,
    });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({
        privateBrowsingLikely: false,
        privateBrowsingReason: "",
        webkitStorageQuota: null,
        jsHeapSizeLimit: null,
      });
    }, FILESYSTEM_PROBE_TIMEOUT_MS);

    try {
      queryUsageAndQuota.call(
        temporaryStorage,
        (_usage, quota) => {
          clearTimeout(timer);
          const storageQuota = Number(quota);
          const jsHeapSizeLimit = Number(performanceValue?.memory?.jsHeapSizeLimit);
          const isMemoryBound =
            Number.isFinite(storageQuota) &&
            storageQuota > 0 &&
            Number.isFinite(jsHeapSizeLimit) &&
            jsHeapSizeLimit > 0 &&
            storageQuota < jsHeapSizeLimit * 2;

          finish({
            privateBrowsingLikely: isMemoryBound,
            privateBrowsingReason: isMemoryBound
              ? "Temporary storage quota appears memory-bound."
              : "",
            webkitStorageQuota: Number.isFinite(storageQuota) ? storageQuota : null,
            jsHeapSizeLimit: Number.isFinite(jsHeapSizeLimit) ? jsHeapSizeLimit : null,
          });
        },
        (error) => {
          clearTimeout(timer);
          finish({
            privateBrowsingLikely: true,
            privateBrowsingReason:
              error?.message || "Temporary storage quota access is blocked.",
            webkitStorageQuota: null,
            jsHeapSizeLimit: null,
          });
        }
      );
    } catch (error) {
      clearTimeout(timer);
      finish({
        privateBrowsingLikely: true,
        privateBrowsingReason:
          error?.message || "Temporary storage quota access failed.",
        webkitStorageQuota: null,
        jsHeapSizeLimit: null,
      });
    }
  });
}

export function getLimitedSessionReason(capabilities) {
  if (!capabilities?.isLimitedSession) return "";
  if (capabilities.privateBrowsingLikely) {
    return "Private browsing may keep sign-in, shared access, and app preferences from saving normally.";
  }
  if (
    capabilities.authPersistenceMode === "memory" ||
    capabilities.authPersistenceMode === "failed"
  ) {
    return "Sign-in may not stay saved in this browser session.";
  }
  return "Private browsing or browser privacy settings may prevent this session from being saved.";
}
